import { useEffect, useLayoutEffect, useRef } from "react";
import { navigate } from "../router.tsx";
import { hashTarget } from "./setting-anchors.ts";

/**
 * A settings screen's URL without a group (or with one the caller cannot see) opens a group in
 * place, without an extra history entry: the group of the setting the anchor names
 * (`#setting-<key>`), else the group a former anchor names (`groupOfAnchor`), else the first
 * one. The anchor is kept, so the row it names gets focus. `group` is null while to redirect.
 */
export function useFirstGroupRedirect<G extends string>(props: {
  group: G | null;
  first: G;
  groupOfKey: (key: string) => G;
  pathOf: (group: G) => string;
  /** A former anchor's group (`#members`), if the screen had any. */
  groupOfAnchor?: (hash: string) => G | null;
}) {
  const { group } = props;
  // The latest mapping, without re-running the effect when a caller passes new closures.
  const latest = useRef(props);
  useLayoutEffect(() => {
    latest.current = props;
  });
  useEffect(() => {
    if (group !== null) {
      return;
    }
    const { first, groupOfKey, pathOf, groupOfAnchor } = latest.current;
    const key = hashTarget();
    const { hash } = window.location;
    const target = key ? groupOfKey(key) : (groupOfAnchor?.(hash) ?? first);
    navigate(`${pathOf(target)}${hash}`, { replace: true });
  }, [group]);
}
